import type { TransactionReportPeriod } from "@/services/reports/retrieveTransactionReport"
import api from "@/services/api"
import type { MaybeRef } from "@vueuse/core"
import { useQuery } from "@tanstack/vue-query"

import { BASE_SUMMARY_REPORT_QUERY_KEY } from "@/services/queryKeys"

type SummaryReportDataPoint = {
  value: number
  comparisonValue: number
  percentageChange: number | null // null if inf change
}

type SummaryReport = {
  orderCount: {
    total: SummaryReportDataPoint
    cash: SummaryReportDataPoint
    card: SummaryReportDataPoint
  }
  revenue: {
    total: SummaryReportDataPoint
    cash: SummaryReportDataPoint
    card: SummaryReportDataPoint
  }
  comparisonDescription: string
}

type SummaryReportApiQuery = {
  from: Date
  until: Date
  compareWithFrom: Date
  compareWithUntil: Date
}

type SummaryReportApiResponse = Omit<
  SummaryReport,
  "comparisonDescription"
>

const getApiQuery = (
  from: Date,
  until: Date,
  comparisonIntervalOffsetDays: number
): SummaryReportApiQuery => ({
  from,
  until,
  compareWithFrom: DateFns.addDays(
    from,
    comparisonIntervalOffsetDays
  ),
  compareWithUntil: DateFns.addDays(
    until,
    comparisonIntervalOffsetDays
  ),
})

export const retrieveSummaryReport = async (
  period: TransactionReportPeriod
): Promise<SummaryReport> => {
  let apiQuery: SummaryReportApiQuery | undefined
  let comparisonDescription: string | undefined

  switch (period) {
    case "Today":
      apiQuery = getApiQuery(
        DateFns.startOfToday(),
        DateFns.endOfToday(),
        -7
      )
      comparisonDescription = "vs previous week"
      break
    case "Yesterday":
      apiQuery = getApiQuery(
        DateFns.startOfYesterday(),
        DateFns.endOfYesterday(),
        -7
      )
      comparisonDescription = "vs previous week"
      break
    case "Last 7 days":
      apiQuery = getApiQuery(
        DateFns.addWeeks(DateFns.startOfToday(), -1),
        DateFns.endOfYesterday(),
        -7
      )
      comparisonDescription = "vs previous week"
      break
    case "Last 28 days":
      apiQuery = getApiQuery(
        DateFns.addWeeks(DateFns.startOfToday(), -4),
        DateFns.endOfYesterday(),
        -28
      )
      comparisonDescription = "vs previous month"
      break
    default:
      apiQuery = getApiQuery(
        DateFns.startOfDay(period.date),
        DateFns.endOfDay(period.date),
        -7
      )
      comparisonDescription = "vs previous week"
  }

  const { body } = await api.get<SummaryReportApiResponse>(
    "/reports/summary/",
    {
      query: apiQuery,
    }
  )

  return { ...body, comparisonDescription }
}

export const useSummaryReport = (
  query: MaybeRef<TransactionReportPeriod>
) =>
  useQuery(
    [BASE_SUMMARY_REPORT_QUERY_KEY, query],
    ({ queryKey: [_name, query_] }) =>
      retrieveSummaryReport(
        unref(query_ as MaybeRef<TransactionReportPeriod>)
      ),
    { refetchInterval: 60 * 1000 }
  )
